import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  listenSosEvents, acknowledgeSos, resolveSos, formatTime,
  phoneHref, waHref,
} from '../services/firebase';
import { isIntertownSos, sosLocationNote, sosMapUrl, sosRowDetails } from '../utils/sosEvents';

export default function SosInbox({ rides, users, onSelectRide, isSuperAdmin = false }) {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('open');
  const [busy, setBusy] = useState(null);

  useEffect(() => listenSosEvents(setEvents), []);

  const rideMap = useMemo(() => {
    const m = {};
    rides.forEach((r) => { m[r.id] = r; });
    return m;
  }, [rides]);

  const userMap = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = u; });
    return m;
  }, [users]);

  const filtered = useMemo(() => events.filter((e) => {
    const status = e.handlingStatus || 'open';
    if (filter === 'open') return status === 'open' || !e.handlingStatus;
    if (filter === 'acked') return status === 'acknowledged';
    if (filter === 'resolved') return status === 'resolved';
    return true;
  }), [events, filter]);

  const openCount = events.filter((e) => !e.handlingStatus || e.handlingStatus === 'open').length;

  const act = async (id, fn, ok) => {
    if (!isSuperAdmin) {
      toast.error('Managing SOS incident status requires Super Admin privileges');
      return;
    }
    setBusy(id);
    try {
      await fn();
      toast.success(ok);
    } catch (err) {
      console.error(err);
      toast.error('Action failed — check Firestore rules for sosEvents');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="toolbar">
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="open">Open ({openCount})</option>
          <option value="acked">Acknowledged</option>
          <option value="resolved">Resolved</option>
          <option value="all">All</option>
        </select>
        <span className="muted">{filtered.length} events</span>
      </div>

      <div className="card alert-card" style={{ marginBottom: 12 }}>
        <div className="card-title">Emergency inbox</div>
        <p className="muted" style={{ fontSize: 13 }}>
          Passenger SOS alerts, local rides and Intertown trips. Call 112 is on their phone — you handle support + follow-up.
          Track links never include OTP.
        </p>
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty">No SOS events in this filter</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>WHEN</th>
                  <th>STATUS</th>
                  <th>PASSENGER</th>
                  <th>RIDE</th>
                  <th>CONTACTS</th>
                  <th>TRACK</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const ride = rideMap[e.rideId];
                  const user = userMap[e.customerId];
                  const details = sosRowDetails(e, { user, ride });
                  const handling = e.handlingStatus || 'open';
                  const track = e.shareToken
                    ? `https://rides.mytownly.in/track/${e.shareToken}`
                    : null;
                  const phone = details.passengerPhone;
                  const mapUrl = sosMapUrl(e);
                  return (
                    <tr key={e.id} className={handling === 'open' ? 'row-urgent' : ''}>
                      <td className="mono">{formatTime(e.createdAt)}</td>
                      <td>
                        {handling === 'open' && <span className="badge red">Open</span>}
                        {handling === 'acknowledged' && <span className="badge orange">Acked</span>}
                        {handling === 'resolved' && <span className="badge green">Resolved</span>}
                        {details.intertown && <span className="badge blue" style={{ marginLeft: 4 }}>Intertown</span>}
                        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                          {details.intertown ? 'Trip' : 'Ride'}: {e.status || '—'}
                        </div>
                      </td>
                      <td>
                        <div>{details.passengerName || '—'}</div>
                        <div className="muted">{phone || e.customerId?.slice(0, 8)}</div>
                      </td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {details.intertown ? (
                          <div>
                            <div>{details.route || 'Intertown trip'}</div>
                            <div className="muted">
                              {[details.annaName && `Anna ${details.annaName}`, details.annaPhone, details.vehicle].filter(Boolean).join(' · ') || '—'}
                            </div>
                            {mapUrl && (
                              <div>
                                <a href={mapUrl} target="_blank" rel="noreferrer">{sosLocationNote(e) || 'Map'}</a>
                              </div>
                            )}
                          </div>
                        ) : onSelectRide && e.rideId ? (
                          <button
                            type="button"
                            className="linkish"
                            style={{ background: 'none', border: 'none', padding: 0, color: '#38bdf8', cursor: 'pointer', fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}
                            onClick={() => onSelectRide({ ...(ride || {}), id: e.rideId })}
                            title="Inspect ride in Rides console"
                          >
                            {e.rideId.slice(0, 8)}… 🔍
                          </button>
                        ) : (
                          <span>{e.rideId?.slice(0, 8)}…</span>
                        )}
                        {ride && (
                          <div className="muted">
                            {ride.pickup?.address?.slice(0, 28) || '—'} → {ride.destination?.name || '—'}
                          </div>
                        )}
                      </td>
                      <td>{e.contactCount ?? '—'}</td>
                      <td>
                        {track ? (
                          <a className="btn btn-ghost btn-sm" href={track} target="_blank" rel="noreferrer">Open</a>
                        ) : '—'}
                      </td>
                      <td>
                        <div className="row-actions">
                          {phoneHref(phone) && <a className="btn btn-ghost btn-sm" href={phoneHref(phone)}>Call</a>}
                          {isIntertownSos(e) && phoneHref(details.annaPhone) && (
                            <a className="btn btn-ghost btn-sm" href={phoneHref(details.annaPhone)}>Call Anna</a>
                          )}
                          {waHref(phone) && (
                            <a className="btn btn-ghost btn-sm" href={waHref(phone, 'Hi, MyTownly support following up on your SOS.')} target="_blank" rel="noreferrer">WA</a>
                          )}
                          {isSuperAdmin && handling === 'open' && (
                            <button
                              type="button"
                              className="btn btn-orange btn-sm"
                              disabled={busy === e.id}
                              onClick={() => act(e.id, () => acknowledgeSos(e.id), 'Acknowledged')}
                            >
                              Ack
                            </button>
                          )}
                          {isSuperAdmin && handling !== 'resolved' && (
                            <button
                              type="button"
                              className="btn btn-green btn-sm"
                              disabled={busy === e.id}
                              onClick={() => act(e.id, () => resolveSos(e.id), 'Resolved')}
                            >
                              Resolve
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
